import { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    TextField, Button, IconButton, InputAdornment,
    Snackbar, Alert, CircularProgress, Typography
} from '@mui/material';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useMutation } from "@apollo/client";
import { FORGOT_PASSWORD_MUTATION, LOGIN_MUTATION} from "../graphql/mutations";
import { motion } from "framer-motion";
import logo from "../assets/images/tks_modern_logo.svg";

const LoginPage = () => {
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [showOtp, setShowOtp] = useState(false);
    const [message, setMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    const [isLoading, setIsLoading] = useState(false);
    const loginBtnRef = useRef(null);
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loginMutation] = useMutation(LOGIN_MUTATION);
    
    // Forgot Password States
    const [isForgetPasswordMode, setIsForgetPasswordMode] = useState(false);
    const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
    const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    // OTP related states
    const [otpSent, setOtpSent] = useState(false);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [countdown, setCountdown] = useState(0);
    
    // Add mutations
    const [resetPasswordMutation] = useMutation(FORGOT_PASSWORD_MUTATION);

    // Countdown timer effect
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        } else if (resendDisabled) {
            setResendDisabled(false);
        }
        return () => clearTimeout(timer);
    }, [countdown, resendDisabled]);

    const startResendTimer = () => {
        setResendDisabled(true);
        setCountdown(30); // 30 seconds countdown
    };

    const handleSendOtp = async () => {
        if (mobile.length !== 10) {
            setMessage("Please enter a valid 10-digit mobile number.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        try {
            const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/send-otp', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ mobile, }),
            });
            const data = await response.json();
            if (data.status === 'error') {
                setMessage("Failed to send OTP. Please try again.");
                setSnackbarSeverity("error");
            }
            setMessage("OTP sent successfully!");
            setSnackbarSeverity("success");
            setOtpSent(true);
            startResendTimer();
          } catch (error) {
            setMessage(error.message || "Failed to send OTP. Please try again.");
            setSnackbarSeverity("error");
          }

       
        setSnackbarOpen(true);
    };

    const handleResendOtp = async () => {
        await handleSendOtp();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (mobile.length !== 10) {
            setMessage("Please enter a valid 10-digit mobile number.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }
        if (!password) {
            setMessage("Please enter your password");
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        setIsLoading(true);
        try {
            const { data } = await loginMutation({ 
                variables: { 
                    Mobile_Number: mobile,
                    password: password
                } 
            });
            
            if (data && data.login) {
                login({ 
                    id: data.login.id,
                    mobile, 
                    role: data.login.Role,
                    isAdmin: data.login.isAdmin,
                    firstName: data.login.First_Name,
                    paymentStatus: data.login.Payment_Status
                });
                
                localStorage.setItem("user_type", data.login.Role);
                localStorage.setItem("first_name", data.login.First_Name);
                localStorage.setItem("payment_status", data.login.Payment_Status);
                localStorage.setItem("mobile", mobile);
                localStorage.setItem("isAdmin", data.login.isAdmin);
                localStorage.setItem("id", data.login.id);
                
                navigate("/landing");
                window.scrollTo(0, 0);
                setMessage("Login Successful!");
                setSnackbarSeverity("success");
            } else {
                throw new Error("Invalid Login");
            }
        } catch (error) {
            setMessage(error.message || "Login failed. Please check your credentials and try again.");
            setSnackbarSeverity("error");
        } finally {
            setIsLoading(false);
        }
        setSnackbarOpen(true);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        
        if (mobile.length !== 10) {
            setMessage("Please enter a valid 10-digit mobile number.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }
        
        if (otp.length !== 6) { // Assuming 6-digit OTP
            setMessage("Please enter a valid 6-digit OTP");
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/verify-otp', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ mobile, otp }),
            });
      
            const data = await response.json();
      
            if (data.status === 'error') {
              setMessage(data.message || "OTP verification failed. Please try again.");
              setSnackbarSeverity("error");
            }
            setForgotPasswordStep(2);
            setMessage("OTP verified successfully!");
            setSnackbarSeverity("success");
          }  catch (error) {
            setMessage(error.message || "OTP verification failed. Please try again.");
            setSnackbarSeverity("error");
        } finally {
            setVerifyOtpLoading(false);
        }
        
        setVerifyOtpLoading(true);
        
        setSnackbarOpen(true);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (newPassword.length < 6) {
            setMessage("Password must be at least 6 characters long");
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        if (newPassword !== confirmPassword) {
            setMessage("Passwords do not match");
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        setResetPasswordLoading(true);
        try {
            const { data } = await resetPasswordMutation({ 
                variables: { 
                    Mobile: mobile,
                    New_Password: newPassword
                } 
            });
            
            if (data) {
                setMessage("Password reset successful! You can now login.");
                setSnackbarSeverity("success");
                setIsForgetPasswordMode(false);
                setForgotPasswordStep(1);
                setNewPassword('');
                setConfirmPassword('');
                setOtp('');
                setOtpSent(false);
            } else {
                throw new Error("Password reset failed");
            }
        } catch (error) {
            setMessage(error.message || "Password reset failed. Please try again.");
            setSnackbarSeverity("error");
        } finally {
            setResetPasswordLoading(false);
        }
        setSnackbarOpen(true);
    };

    const toggleForgetPasswordMode = () => {
        setIsForgetPasswordMode(!isForgetPasswordMode);
        setForgotPasswordStep(1);
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setOtpSent(false);
    };
    
    return (
        <div className="min-h-screen flex flex-col md:flex-row items-center justify-center bg-gradient-to-br from-yellow-100 via-yellow-200 to-yellow-300">
            <div className="hidden md:flex w-1/2 justify-center items-center px-10">
                <div className="max-w-md text-center">
                    <img src={logo} alt="Logo" className="mx-auto w-auto h-80 mb-6" />
                    <Typography variant="h4" sx={{ fontWeight: "600" }}>Hello Again!</Typography>
                    <Typography sx={{ mt: 2 }}>Explore the platform with a secure and simple login.</Typography>
                </div>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full md:w-1/2 flex justify-center items-center px-6 py-12"
            >
                <div className="backdrop-blur-md bg-white/30 border border-yellow-600 shadow-2xl rounded-3xl p-8 w-full max-w-md relative"
                    style={{
                        background: 'rgba(255, 255, 255, 0.7)',
                    }}
                >
                    <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white rounded-full px-4 py-1 text-sm shadow-lg">
                        {isForgetPasswordMode ? 'RESET PASSWORD' : 'LOGIN'}
                    </div>
                    <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 2 }}>
                        {isForgetPasswordMode 
                            ? (forgotPasswordStep === 1 ? 'Verify Your Identity' : 'Reset Your Password') 
                            : 'Welcome Back'}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", textAlign: "center", mb: 4, color: "#555" }}>
                        {isForgetPasswordMode 
                            ? (forgotPasswordStep === 1 ? 'Enter your mobile number and OTP' : 'Create a new password') 
                            : 'Sign in to continue'}
                    </Typography>
                    
                    {/* Conditional Form Based on Mode */}
                    {!isForgetPasswordMode ? (
                        // Login Form
                        <form onSubmit={handleSubmit}>
                            <TextField
                                label="Mobile Number"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                inputProps={{ maxLength: 10, pattern: "[0-9]*" }}
                                onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgba(0, 0, 0, 0.6)',
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiInputLabel-root': {
                                        color: '#rgba(0, 0, 0, 0.6)',
                                    },
                                }}
                                required
                            />
                            <TextField
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                fullWidth
                                size="small"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                }}
                                required
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(prev => !prev)}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Button
                                type="submit"
                                ref={loginBtnRef}
                                fullWidth
                                variant="contained"
                                sx={{
                                    background: 'linear-gradient(135deg, #B4AB57, #947F49)',
                                    fontWeight: 'bold',
                                    borderRadius: '999px',
                                    py: 1.5,
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #947F49, #B4AB57)',
                                    }
                                }}
                                disabled={isLoading}
                            >
                                {isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Login'}
                            </Button>
                        </form>
                    ) : (
                        // Forgot Password Form - Two Steps
                        forgotPasswordStep === 1 ? (
                            // Step 1: Mobile Number and OTP Verification
                            <form onSubmit={handleVerifyOtp}>
                                <div className="flex gap-2 mb-2">
                                    <TextField
                                        label="Mobile Number"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value)}
                                        inputProps={{ maxLength: 10, pattern: "[0-9]*" }}
                                        onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                            },
                                        }}
                                        required
                                        disabled={otpSent}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={otpSent ? handleResendOtp : handleSendOtp}
                                        disabled={resendDisabled || mobile.length !== 10}
                                        sx={{
                                            minWidth: '100px',
                                            background: 'linear-gradient(135deg, #B4AB57, #947F49)',
                                            fontWeight: 'bold',
                                            borderRadius: '10px',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #947F49, #B4AB57)',
                                            }
                                        }}
                                    >
                                        {resendDisabled ? `${countdown}s` : otpSent ? 'Resend' : 'Send OTP'}
                                    </Button>
                                </div>
                                <TextField
                                    label="OTP"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
                                    onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6)}
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                        },
                                    }}
                                    required
                                    disabled={!otpSent}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        background: 'linear-gradient(135deg, #B4AB57, #947F49)',
                                        fontWeight: 'bold',
                                        borderRadius: '999px',
                                        py: 1.5,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #947F49, #B4AB57)',
                                        }
                                    }}
                                    disabled={verifyOtpLoading || !otpSent || otp.length !== 6}
                                >
                                    {verifyOtpLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Verify OTP'}
                                </Button>
                            </form>
                        ) : (
                            // Step 2: New Password and Confirm Password
                            <form onSubmit={handleResetPassword}>
                                <TextField
                                    label="New Password"
                                    type={showNewPassword ? "text" : "password"}
                                    fullWidth
                                    size="small"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                        },
                                    }}
                                    required
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowNewPassword(prev => !prev)}>
                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <TextField
                                    label="Confirm Password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    fullWidth
                                    size="small"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                        },
                                    }}
                                    required
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowConfirmPassword(prev => !prev)}>
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        background: 'linear-gradient(135deg, #B4AB57, #947F49)',
                                        fontWeight: 'bold',
                                        borderRadius: '999px',
                                        py: 1.5,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #947F49, #B4AB57)',
                                        }
                                    }}
                                    disabled={resetPasswordLoading}
                                >
                                    {resetPasswordLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Reset Password'}
                                </Button>
                            </form>
                        )
                    )}
                    
                    {/* Links section */}
                    <div className="mt-3">
                        <Typography sx={{ fontSize: "14px", mb: 1 }}>
                            Don't have an account? <a onClick={() => {navigate("/register");window.scrollTo(0, 0);}} className="text-yellow-700 font-semibold cursor-pointer">Register</a>
                        </Typography>
                        <Typography sx={{ fontSize: "14px" ,mt:2}}>
                            <a onClick={toggleForgetPasswordMode} className="text-yellow-700 font-semibold cursor-pointer">
                                {isForgetPasswordMode ? 'Back to Login' : 'Forgot Password?'}
                            </a>
                        </Typography>
                    </div>
                    
                    <Typography sx={{ mt: 2, textAlign: "center", fontSize: "12px", color: "#000" }}>
                        Trouble logging in? <a href="/help" className="text-yellow-700 font-semibold">Get Help.</a>
                    </Typography>
                </div>
            </motion.div>
            
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default LoginPage;